import { Box, Empty, Flex, Group, IconFont, Input, Modal, Text, Tooltip } from '@byecode/ui'
import type { PageVariableSchema } from '@lighthouse/core'
import { TitleInContentModal } from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import React, { useCallback } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import styled from 'styled-components'
import { useImmer } from 'use-immer'

import { PopoverQuestion } from '@/components/PopoverQuestion'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'

import type { PageParameterFormSlice } from '../formSlice'
import { VariableForm } from './components/VariableForm'

const Link = styled.a`
    display: inline-block;
    color: var(--color-main);
    line-height: 22px;
`

const IconButton = styled.button`
    display: inline-flex;
    outline: none;
    border: none;
    padding: 8px;
    background-color: var(--color-gray-100);
    border-radius: 6px;
`

const INIT_FORM_MODAL_STATE = { open: false, editData: null, index: 0 }

export const Variable = () => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)

    const { control } = useFormContext<PageParameterFormSlice>()
    const { fields, append, remove, update } = useFieldArray({
        control,
        name: 'parameterAndVariable.variables',
        keyName: 'key'
    })

    const [formModalState, setFormModalState] = useImmer<{ open: boolean; editData: PageVariableSchema | null; index: number }>(
        INIT_FORM_MODAL_STATE
    )

    const onConfirm = useCallback(
        (values: PageVariableSchema) => {
            if (formModalState.editData) {
                const editId = formModalState.editData.id
                const index = fields.findIndex(item => item.id === editId)
                if (index === -1) {
                    return
                }

                update(index, values)
            } else {
                append({ ...values, id: nanoid() })
            }
            setFormModalState(INIT_FORM_MODAL_STATE)
        },
        [append, fields, formModalState.editData, setFormModalState, update]
    )

    return (
        <Group
            label={
                <PopoverQuestion
                    type="pageVariable"
                    renderTarget={(node, ref) => (
                        <Flex alignItems="center" gap={6} ref={ref}>
                            <IconFont type="PageDatabase" size={16} fill="var(--color-gray-400)" />
                            <Text>页面数据</Text>
                            {node}
                        </Flex>
                    )}
                />
            }
            extra={
                <IconFont
                    type="Add"
                    onClick={e => {
                        e.stopPropagation()
                        setFormModalState(s => {
                            s.open = true
                            s.index = fields.length
                        })
                    }}
                />
            }
            styles={{
                root: {
                    padding: 0,
                    overflow: 'hidden',
                    borderRadius: 8,
                    border: '1px solid var(--color-gray-200)'
                },
                label: {
                    fontWeight: 400
                },
                wrapper: {
                    backgroundColor: 'var(--color-gray-200)'
                },
                collapse: {
                    padding: '8px 16px !important'
                }
            }}
        >
            {fields.length === 0 ? (
                <Empty
                    icon="PageDatabase"
                    title={
                        <>
                            <Text>查询一条数据，供页面中的组件使用</Text>
                            <Link
                                href="https://byecodehelp.yuque.com/org-wiki-byecodehelp-zavfcl/oevich/edd08zh08mf41h2o"
                                target="_blank"
                                rel="noreferrer"
                            >
                                {'了解更多>'}
                            </Link>
                        </>
                    }
                    styles={{
                        title: {
                            textAlign: 'center',
                            fontWeight: '400 !important',
                            color: 'var(--color-gray-400)'
                        },
                        wrapper: {
                            padding: 12
                        }
                    }}
                />
            ) : (
                fields.map((item, index) => (
                    <Flex key={item.id} gap={8} alignItems="center" py={4}>
                        <Text mr="auto" size={14}>
                            {item.name}
                        </Text>

                        <Input
                            readOnly
                            focusOutLine={false}
                            value={dataSourceList.find(ds => ds.id === item.dsId)?.name}
                            styles={{ wrapper: { width: 140 }, input: { cursor: 'pointer' } }}
                            prefix={
                                <Box p={4} style={{ display: 'inline-flex', borderRadius: 4, backgroundColor: 'var(--color-main)' }}>
                                    <IconFont type="QueryDataBase" fill="var(--color-white)" />
                                </Box>
                            }
                            onClick={() => {
                                setFormModalState({ open: true, editData: item, index })
                            }}
                        />

                        <Tooltip title="删除">
                            <IconButton
                                onClick={async () => {
                                    const isConfirm = await Modal.confirm({
                                        title: `确认删除“${item.name}”？`,
                                        content: '删除后，将影响与该变量相关的所有设置和数据。请确认是否继续。'
                                    })
                                    if (isConfirm) {
                                        remove(index)
                                    }
                                }}
                            >
                                <IconFont type="Trash" fill="var(--color-gray-400)" />
                            </IconButton>
                        </Tooltip>
                    </Flex>
                ))
            )}

            <TitleInContentModal
                open={formModalState.open}
                onClose={() => setFormModalState(INIT_FORM_MODAL_STATE)}
                title={`${formModalState.editData ? '编辑' : '添加'}页面数据`}
                styles={{
                    desktop: {
                        modal: {
                            maxHeight: '92% !important'
                        },
                        body: {
                            overflow: 'hidden !important',
                            display: 'flex',
                            flexDirection: 'column'
                        }
                    }
                }}
            >
                <VariableForm
                    dataSourceList={dataSourceList}
                    defaultValues={formModalState.editData}
                    index={formModalState.index}
                    onCancel={() => setFormModalState(INIT_FORM_MODAL_STATE)}
                    onConfirm={onConfirm}
                />
            </TitleInContentModal>
        </Group>
    )
}
